import moment from "moment";
import {
  MedicalPaOrder,
  PatientResponsibility,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import { CoverageStatus } from "../../../enums/coverageStatus";
import GreenBullet from "../../../svg/green-bullet";
import RedBullet from "../../../svg/red-bullet";
import { formatDate } from "../../../utils/formatDate";
import { generateFullName } from "../../../utils/generateOrderDataForTable";

export const generateCoverageInfoForMedicalPaOrder = (
  order: MedicalPaOrder,
) => {
  const coverage = order?.coverage ?? undefined;

  if (!coverage) return [];

  const preferredOrder = ["primary", "secondary", "tertiary"];
  const orderedCoverages = preferredOrder.filter((key) => key in coverage);

  const allCoveragesData = orderedCoverages.map((key) => {
    return generateRequireDataFromSingleCoverage(coverage[key], order);
  });

  return allCoveragesData;
};

export const generateRequireDataFromSingleCoverage = (
  coverage: SingleCoverage,
  order: MedicalPaOrder,
) => {
  const payerName = coverage?.payer_name ?? "";
  const coverageStatus = mapCoverageStatus(coverage?.status ?? "");

  const primaryData = generatePrimaryData(coverage, order);
  const deductibleData = generateDeductibleData(
    coverage?.patient_responsibility ?? {},
  );
  const outOfPocketData = generateOutOfPocketData(
    coverage?.patient_responsibility ?? {},
  );

  const costSharingData = generateCostSharingData(
    coverage?.patient_responsibility ?? {},
  );

  const coInsuranceData = getCoInsuranceData(
    coverage?.patient_responsibility ?? {},
  );

  // Determine if this coverage is active or inactive using existing function
  const isActive = determineStausCodeForCoverage(
    coverage?.coverage_status ?? "",
  );

  const dataToSend = {
    payerName,
    coverageStatus,
    isActive, // Use active/inactive based on coverage status
    primaryData,
    coInsurance: coInsuranceData,
    sharedAmountData: costSharingData,
    costSharingName: "Chemotherapy",
    coverageAmountRelatedData: [
      {
        deductible: deductibleData,
        outOfPocket: outOfPocketData,
      },
    ],
  };

  return dataToSend;
};

const mapCoverageStatus = (status: string): CoverageStatus => {
  if (status === "in_progress") return CoverageStatus.IN_PROGRESS;
  if (status === "pending") return CoverageStatus.PENDING;
  if (status === "approved") return CoverageStatus.APPROVED;
  if (status === "rejected") return CoverageStatus.REJECTED;
  if (status === "error") return CoverageStatus.ERROR;
  if (status === "completed") return CoverageStatus.COMPLETED;
  if (status === "inactive") return CoverageStatus.INACTIVE;
  if (status === "ev_type_not_supported")
    return CoverageStatus.EV_TYPE_NOT_SUPPORTED;
  if (status.toLowerCase().includes("inactive")) return CoverageStatus.INACTIVE;
  if (status.toLowerCase().includes("error")) return CoverageStatus.ERROR;
  return CoverageStatus.UNKNOWN;
};

const generatePrimaryData = (
  coverage: SingleCoverage,
  order: MedicalPaOrder,
) => {
  const data = [
    {
      header: "Date of Birth",
      text: moment(order?.demographics?.date_of_birth).format("MM/DD/YYYY"),
    },
    {
      header: "Member ID",
      text: coverage?.member_id ?? "",
    },
    {
      header: "Status",
      text: StatusCodeMapper(
        determineStausCodeForCoverage(coverage?.coverage_status ?? ""),
      ),
      textColor: statusCodeColorMapper(
        determineStausCodeForCoverage(coverage?.coverage_status ?? ""),
      ),
      icon: statusCodeIconMapper(
        determineStausCodeForCoverage(coverage?.coverage_status ?? ""),
      ),
      fontWeight: "700",
    },
    {
      header: "Provider",
      text: generateFullName(
        order?.requesting_practitioner_details?.first_name ?? "",
        "",
        order?.requesting_practitioner_details?.last_name ?? "",
      ),
    },
    {
      header: "Provider NPI",
      text: order?.requesting_practitioner_details?.npi ?? "",
    },
    {
      header: "Service Type",
      text: order?.payload?.service_type ?? "",
    },
    {
      header: "Effective Date",
      text: formatDate(
        coverage?.service_year_from,
        "MM/DD/YYYY",
        "Not Available",
      ),
    },
    {
      header: "Plan End Date",
      text: formatDate(
        coverage?.patient_responsibility?.service_year_to,
        "MM/DD/YYYY",
        "Not Available",
      ),
    },
    {
      header: "Plan Name",
      text: coverage?.plan_name ?? "",
    },
    {
      header: "Date of Service",
      text: order?.payload?.date_of_service
        ? moment(order?.payload?.date_of_service).format("MM/DD/YYYY")
        : "",
    },
  ];

  return data;
};

const StatusCodeMapper = (status: "active" | "inactive" | "unknown") => {
  if (status === "active") {
    return "Active";
  } else if (status === "inactive") {
    return "Inactive";
  } else {
    return "Not Available";
  }
};

const statusCodeColorMapper = (status: "active" | "inactive" | "unknown") => {
  if (status === "active") {
    return "#008367";
  } else if (status === "inactive") {
    return "#CD0202";
  } else {
    return "#000000";
  }
};

const statusCodeIconMapper = (
  status: "active" | "inactive" | "unknown",
): React.JSX.Element => {
  if (status === "active") {
    return GreenBullet({ color: "#008367" });
  } else if (status === "inactive") {
    return RedBullet();
  } else {
    return GreenBullet({ color: "#6d6d6d" });
  }
};

export const determineStausCodeForCoverage = (
  status?: string,
): "active" | "inactive" | "unknown" => {
  if (status && typeof status === "string") {
    if (status === "") return "inactive";
    if (status === "-") return "unknown";
    if (status.toLowerCase().includes("inactive")) return "inactive";
    if (status.toLowerCase().includes("active")) return "active";
    return "unknown";
  }
  return "unknown";
};

const generateDeductibleData = (data: PatientResponsibility) => {
  const deductibleType = getNetworkTypeForAmounts(
    data?.deductibles_network_type ?? "",
  );

  // if (deductibleType === "Unknown") return {};

  const individualDataForDeductible = {
    type: "Individual",
    amountType: getNetworkTypeForUI(data?.deductibles_network_type ?? ""),
    card: [
      {
        header: "Total",
        text: parseAmountToDollars(data?.individual_deductibles ?? ""),
      },
      {
        header: "Used",
        text: parseAmountToDollars(data?.individual_deductibles_met ?? ""),
      },
    ],
  };

  const familyDataForDeductible = {
    type: "Family",
    amountType: getNetworkTypeForUI(data?.deductibles_network_type ?? ""),
    card: [
      {
        header: "Total",
        text: parseAmountToDollars(data?.family_deductibles ?? ""),
      },
      {
        header: "Used",
        text: parseAmountToDollars(data?.family_deductibles_met ?? ""),
      },
    ],
  };

  // Safely convert to numbers with proper validation
  const individualDeductible =
    parseFloat(data?.individual_deductibles ?? "0") || 0;

  const individualDeductibleMet =
    parseFloat(data?.individual_deductibles_met ?? "0") || 0;

  const totalDeductible = individualDeductible;
  const totalDeductibleUsed = individualDeductibleMet;

  const dataToSend = {
    [deductibleType]: [individualDataForDeductible, familyDataForDeductible],
    totalAmount: totalDeductible === 0 ? 0 : totalDeductible.toFixed(3),
    totalAmountUsed:
      totalDeductibleUsed === 0 ? 0 : totalDeductibleUsed.toFixed(3),
  };

  return dataToSend;
};

const generateOutOfPocketData = (data: PatientResponsibility) => {
  const outOfPocketType = getNetworkTypeForAmounts(
    data?.out_of_pocket_network_type ?? "",
  );

  // if (outOfPocketType === "Unknown") return {};

  const individualDataForOutOfPocket = {
    type: "Individual",
    amountType: getNetworkTypeForUI(data?.out_of_pocket_network_type ?? ""),
    card: [
      {
        header: "Total",
        text: parseAmountToDollars(data?.individual_out_of_pocket ?? ""),
      },
      {
        header: "Used",
        text: parseAmountToDollars(data?.individual_out_of_pocket_met ?? ""),
      },
    ],
  };

  const familyDataForOutOfPocket = {
    type: "Family",
    amountType: getNetworkTypeForUI(data?.out_of_pocket_network_type ?? ""),
    card: [
      {
        header: "Total",
        text: parseAmountToDollars(data?.family_out_of_pocket ?? ""),
      },
      {
        header: "Used",
        text: parseAmountToDollars(data?.family_out_of_pocket_met ?? ""),
      },
    ],
  };

  // Safely convert to numbers with proper validation
  const individualOutOfPocket =
    parseFloat(data?.individual_out_of_pocket ?? "0") || 0;

  const individualOutOfPocketMet =
    parseFloat(data?.individual_out_of_pocket_met ?? "0") || 0;

  const totalOutOfPocket = individualOutOfPocket;
  const totalOutOfPocketUsed = individualOutOfPocketMet;

  const dataToSend = {
    [outOfPocketType]: [individualDataForOutOfPocket, familyDataForOutOfPocket],
    totalAmount: totalOutOfPocket === 0 ? 0 : totalOutOfPocket.toFixed(3),
    totalAmountUsed:
      totalOutOfPocketUsed === 0 ? 0 : totalOutOfPocketUsed.toFixed(3),
  };

  return dataToSend;
};

const getNetworkTypeForUI = (networkType: string) => {
  if (networkType === "inNetwork" || networkType === "in_network")
    return "In Network";
  if (networkType === "outNetwork" || networkType === "out_network")
    return "Out Network";
  if (networkType === "noNetwork" || networkType === "no_network")
    return "No Network";
  if (
    networkType === "notApplicable" ||
    networkType === "not_applicable" ||
    networkType === "notApplicableNetwork"
  )
    return "Not Applicable";
  return "Unknown";
};

const getNetworkTypeForAmounts = (networkType: string) => {
  if (networkType === "inNetwork" || networkType === "in_network")
    return "inNetwork";
  if (networkType === "outNetwork" || networkType === "out_network")
    return "outNetwork";
  if (networkType === "noNetwork" || networkType === "no_network")
    return "noNetwork";
  if (
    networkType === "notApplicable" ||
    networkType === "not_applicable" ||
    networkType === "notApplicableNetwork"
  )
    return "notApplicable";
  return "unknown";
};

const parseAmountToDollars = (amount: string) => {
  const cleanedAmount = cleanAmountData(amount);
  if (cleanedAmount) {
    if (cleanedAmount === "-") return "$0";
    if (cleanedAmount === "") return "$0";
    if (cleanedAmount === "0") return "$0";
    const amountInNumber = Number(cleanedAmount);
    if (isNaN(amountInNumber)) return "$0";
    return `$${amountInNumber.toFixed(3)}`;
  } else {
    return "$0";
  }
};

const getCoInsuranceData = (data: PatientResponsibility) => {
  const cleanedAmount = cleanAmountData(data?.co_insurance ?? "");
  if (cleanedAmount) {
    return cleanedAmount === "-" || cleanedAmount === "0.0"
      ? "0%"
      : `${cleanedAmount}%`;
  } else {
    return "0%";
  }
};

const generateCostSharingData = (data: PatientResponsibility) => {
  const sharingData = [
    {
      header: "Copay",
      text: parseAmountToDollars(data?.co_pay ?? ""),
    },
    {
      header: "Co-Insurance",
      text: getCoInsuranceData(data),
    },
  ];

  return sharingData;
};

const cleanAmountData = (amount: string) => {
  return amount.replace(/[^0-9.-]+/g, "");
};
